import type { PointerEvent } from 'react'
import clsx from 'clsx'
import { currentEvaluation, playedTiles, useGameState } from '@/hooks/useGameState'
import { sourceKey, startTileDrag, useDrag } from '@/hooks/useTileDrag'
import { CELL, letterValue } from '@/lib/scoring'

// Colours come from the theme (src/index.css).
const BONUS: Record<number, { label: string; className: string }> = {
  [CELL.doubleLetter]: { label: '2L', className: 'cell-2l' },
  [CELL.tripleLetter]: { label: '3L', className: 'cell-3l' },
  [CELL.doubleWord]: { label: '2W', className: 'cell-2w' },
  [CELL.tripleWord]: { label: '3W', className: 'cell-3w' },
}

export function GameBoard() {
  const state = useGameState()
  const { puzzle, board, placed, selected, cursor, record, view, coach, place, recallAt, setCursor } = state
  const over = useDrag((d) => d.over)
  const dragging = useDrag((d) => d.ghost?.from)
  if (!puzzle) return null

  const evaluation = currentEvaluation(state)
  const placedAt = new Map(placed.map((p) => [`${p.row},${p.col}`, puzzle.player_letters[p.rackIndex]]))
  const cols = puzzle.board_width
  const finished = record?.finished ?? false
  // Once the day is played, the board shows the player's play or the top play.
  const shownTiles = !finished ? [] : view === 'top' ? (puzzle.best_play?.tiles ?? []) : playedTiles(record)
  const shown = new Map<string, string>(shownTiles.map((t) => [`${t.row},${t.col}`, t.letter]))
  const rows = puzzle.board_height

  return (
    // The board area takes the height the page leaves; the board is as large as
    // fits in it (and at most 4.5rem a square), so the controls always stay on screen.
    <div className="flex flex-[1_0_12rem] items-center [container-type:size]">
      <div
        className="board-frame mx-auto"
        style={{ width: `min(100cqw, (100cqh - var(--frame)) * ${cols / rows} + var(--frame), ${cols * 4.5}rem + var(--frame))` }}
      >
        <div style={{ containerType: 'inline-size' }}>
          <div
            role="group"
            aria-label="Game board"
            className="board-grid grid"
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, fontSize: `calc(100cqw / ${cols} * 0.5)` }}
          >
          {puzzle.board_layout.map((row, r) =>
            row.map((cell, c) => {
              const key = `${r},${c}`
              const fixed = board[r][c]
              const mine = placedAt.get(key)
              const bonus = BONUS[cell]
              const canDrop = cell !== CELL.blocked && !fixed && !mine
              const atCursor = cursor?.row === r && cursor.col === c

              if (cell === CELL.blocked) {
                return (
  <div key={key} role="img" aria-label="Blocked square" className="cell-blocked aspect-square" />
                )
              }
              if (fixed) {
                return (
                  <Tile
                    key={key}
                    letter={fixed}
                    label={`Row ${r + 1}, column ${c + 1}, ${fixed}`}
                    onClick={finished ? undefined : () => setCursor(r, c)}
                    className={clsx('tile-board', atCursor && 'is-cursor')}
                  />
                )
              }
              const played = shown.get(key)
              if (played) {
                return view === 'top' ? (
                  <Tile key={key} letter={played} label={`Row ${r + 1}, column ${c + 1}, ${played} from the top play`} className="tile-reveal" />
                ) : (
                  <Tile key={key} letter={played} label={`Row ${r + 1}, column ${c + 1}, ${played} from your play`} className="tile-mine" />
                )
              }
              if (mine) {
                const from = { row: r, col: c }
                return (
                  <Tile
                    key={key}
                    letter={mine}
                    label={`${mine}, tap to return to rack`}
                    onClick={() => recallAt(r, c)}
                    onPointerDown={(e) => startTileDrag(e, from, mine)}
                    // Another tile dropped here swaps with (or, from the rack, replaces) this one.
                    dropKey={key}
                    className={clsx(
                      'tile-mine touch-none',
                      evaluation?.ok && 'valid',
                      atCursor && 'is-cursor',
                      dragging === sourceKey(from) && 'opacity-30',
                      over === key && dragging !== sourceKey(from) && 'is-drop',
                    )}
                  />
                )
              }
              return (
                <button
                  key={key}
                  type="button"
                  aria-label={`Row ${r + 1}, column ${c + 1}${bonus ? `, ${bonus.label}` : ''}`}
                  disabled={finished}
                  onClick={() => (selected !== null ? place(r, c) : setCursor(r, c))}
                  data-cell={canDrop && !finished ? key : undefined}
                  className={clsx(
                    'cell relative flex aspect-square items-center justify-center',
                    bonus?.className,
                    over === key && 'is-drop',
                  coach?.cell === key && 'coach-target',
                    atCursor && 'is-cursor',
                  )}
                >
                  {bonus && <span className="cell-label text-[0.55em]">{bonus.label}</span>}
                  {atCursor && (
                    <span aria-hidden className="cursor-arrow absolute text-[0.9em]">
                      {cursor.dir === 'across' ? '→' : '↓'}
                    </span>
                  )}
                </button>
              )
            }),
          )}
          </div>
        </div>
      </div>
    </div>
  )
}

interface TileProps {
  letter: string
  className: string
  label?: string
  onClick?: () => void
  onPointerDown?: (e: PointerEvent<HTMLElement>) => void
  /** Makes the tile a drop target, as "row,col". */
  dropKey?: string
}

function Tile({ letter, className, label, onClick, onPointerDown, dropKey }: TileProps) {
  const Element = onClick ? 'button' : 'div'
  return (
    <Element
      type={onClick ? 'button' : undefined}
      role={onClick ? undefined : 'img'}
      aria-label={label ?? letter}
      onClick={onClick}
      onPointerDown={onPointerDown}
      data-cell={dropKey}
      className={clsx('tile relative flex aspect-square items-center justify-center', className)}
    >
      <span className="text-[1.1em] leading-none">{letter}</span>
      <span className="absolute bottom-[6%] right-[10%] text-[0.45em] leading-none opacity-70">{letterValue(letter)}</span>
    </Element>
  )
}
